import React, { useState, useEffect, useContext } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import BlockIcon from '@mui/icons-material/Block';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import DataTable from '../../../sharedModules/dataTable/DataTable/DataTable';
import { IDataTableColumn } from '../../../sharedModules/dataTable/DataTable/model';
import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

const columns: IDataTableColumn<any>[] = [
  {
    name: 'Subject',
    selector: 'subject',
    sortable: true,
    wrap: true,
    width: '350px',
    format: (row: any) => (
      <Link
        to={`/vendors/outlook-emails/${row.id}`}
        style={{ color: 'inherit', textDecoration: 'none' }}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {row.subject}
        {row.ignored_at && (
          <Chip
            icon={<BlockIcon />}
            label="Ignored"
            size="small"
            color="default"
            variant="outlined"
            style={{ marginLeft: 8 }}
          />
        )}
      </Link>
    ),
  },
  {
    name: 'From',
    selector: 'from_name',
    sortable: true,
    wrap: true,
    format: (row: any) => {
      return (
        <div>
          <div style={{ fontWeight: 500 }}>{row.from_name || '-'}</div>
          <div style={{ fontSize: '0.85em', color: '#666' }}>{row.from_address || '-'}</div>
        </div>
      );
    },
  },
  {
    name: 'Account',
    selector: 'email_account',
    sortable: true,
    wrap: true,
  },
  {
    name: 'Vendor',
    selector: 'vendor_name',
    sortable: true,
    wrap: true,
    format: (row: any) => row.vendor_name || '-',
  },
  {
    name: 'Received',
    selector: 'received_at',
    sortable: true,
    format: (row: any) => {
      if (!row.received_at) return '-';
      const date = new Date(row.received_at);
      return (
        <div>
          <div>{date.toLocaleDateString()}</div>
          <div style={{ fontSize: '0.85em', color: '#666' }}>{date.toLocaleTimeString()}</div>
        </div>
      );
    },
  },
  {
    name: 'Attachments',
    selector: 'has_attachments',
    sortable: true,
    format: (row: any) => {
      if (row.has_attachments) {
        return <AttachFileIcon fontSize="small" color="action" />;
      }
      return '-';
    },
  },
];

const EmailReviewPage: React.FunctionComponent = () => {
  const history = useHistory();
  const location = useLocation();
  const { showErrorSnackBar } = useContext(SnackBarContext);

  const queryParams = new URLSearchParams(location.search);
  const initialSearch = queryParams.get('search') || '';
  const initialAccount = queryParams.get('account') || 'all';
  const initialVendorMatch = queryParams.get('vendor_matched') || 'false';
  const initialShowIgnored = queryParams.get('show_ignored') === 'true';

  const [emails, setEmails] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>(initialSearch);
  const [accountFilter, setAccountFilter] = useState<string>(initialAccount);
  const [vendorMatchFilter, setVendorMatchFilter] = useState<string>(initialVendorMatch);
  const [showIgnored, setShowIgnored] = useState<boolean>(initialShowIgnored);
  const [loading, setLoading] = useState<boolean>(false);

  const [accounts, setAccounts] = useState<string[]>([]);

  const updateUrl = (search: string, account: string, vendorMatched: string, includeIgnored: boolean) => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (account !== 'all') params.set('account', account);
    if (vendorMatched !== 'false') params.set('vendor_matched', vendorMatched);
    if (includeIgnored) params.set('show_ignored', 'true');
    const qs = params.toString();
    history.replace(`/vendors/email-review${qs ? `?${qs}` : ''}`);
  };

  const fetchEmails = async (search = '', account = 'all', vendorMatched = 'false', includeIgnored = false) => {
    setLoading(true);
    try {
      const params: any = { has_attachments: 'true' };
      if (search) params.search = search;
      if (account !== 'all') params.email_account = account;
      if (vendorMatched !== 'all') params.vendor_matched = vendorMatched;
      if (includeIgnored) params.include_ignored = 'true';

      const response: any = await axios.get('/vendors/outlook_emails', { params });
      const emailData = response.data || [];
      setEmails(emailData);

      const uniqueAccounts = [...new Set(emailData.map((e: any) => e.email_account))].filter(Boolean);
      setAccounts(uniqueAccounts as string[]);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to fetch emails';
      showErrorSnackBar(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    updateUrl(searchTerm, accountFilter, vendorMatchFilter, showIgnored);
    fetchEmails(searchTerm, accountFilter, vendorMatchFilter, showIgnored);
  };

  const handleAccountChange = (newAccount: string) => {
    setAccountFilter(newAccount);
    updateUrl(searchTerm, newAccount, vendorMatchFilter, showIgnored);
    fetchEmails(searchTerm, newAccount, vendorMatchFilter, showIgnored);
  };

  const handleVendorMatchChange = (newFilter: string) => {
    setVendorMatchFilter(newFilter);
    updateUrl(searchTerm, accountFilter, newFilter, showIgnored);
    fetchEmails(searchTerm, accountFilter, newFilter, showIgnored);
  };

  const handleShowIgnoredChange = (checked: boolean) => {
    setShowIgnored(checked);
    updateUrl(searchTerm, accountFilter, vendorMatchFilter, checked);
    fetchEmails(searchTerm, accountFilter, vendorMatchFilter, checked);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  useEffect(() => {
    fetchEmails(initialSearch, initialAccount, initialVendorMatch, initialShowIgnored);
  }, []);

  return (
    <Box style={{ width: '100%' }}>
      <Box>
        <Grid justifyContent="space-between" container spacing={3}>
          <Grid item>
            <Typography variant="h5" component="h5" style={{ marginTop: 5 }}>
              Email Review
            </Typography>
          </Grid>
        </Grid>
        <br />
        <Grid container spacing={2} alignItems="flex-start" onKeyDown={handleKeyPress} style={{ marginBottom: 16 }}>
          <Grid item xs={12} md={3}>
            <TextField
              label="Search by Subject, Name, or Email"
              variant="standard"
              style={{ width: '100%', marginTop: 8 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Enter search term"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl variant="standard" style={{ width: '100%', marginTop: 8 }}>
              <InputLabel>Email Account</InputLabel>
              <Select
                value={accountFilter}
                onChange={(e) => handleAccountChange(e.target.value as string)}
                label="Email Account"
              >
                <MenuItem value="all">All Accounts</MenuItem>
                {accounts.map((account) => (
                  <MenuItem key={account} value={account}>
                    {account}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl variant="standard" style={{ width: '100%', marginTop: 8 }}>
              <InputLabel>Vendor Match</InputLabel>
              <Select
                value={vendorMatchFilter}
                onChange={(e) => handleVendorMatchChange(e.target.value as string)}
                label="Vendor Match"
              >
                <MenuItem value="false">Unmatched</MenuItem>
                <MenuItem value="true">Matched</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showIgnored}
                  onChange={(e) => handleShowIgnoredChange(e.target.checked)}
                  size="small"
                />
              }
              label="Show Ignored"
              style={{ marginTop: 18 }}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <Button onClick={handleSearch} variant="contained" style={{ marginTop: 20 }}>
              Search
            </Button>
          </Grid>
        </Grid>
        <Card>
          <CardContent>
            <DataTable
              title="Emails"
              columns={columns}
              data={emails}
              onRowClicked={(row) => history.push(`/vendors/outlook-emails/${row.id}`)}
              striped
              dense
              highlightOnHover
              pointerOnHover
              noHeader
              pagination
              progressPending={loading}
            />
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default EmailReviewPage;
